import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, debounceTime, distinctUntilChanged, filter, finalize, takeUntil } from 'rxjs';
import { IconModule } from 'src/app/shared/icon/icon.module';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListModuleFilters,
  ListModulePaginationMeta,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
} from 'src/app/shared/components/list-module';
import {
  EmailTemplateApiService,
  EmailTemplateRow,
} from 'src/app/core/services/email-template-api.service';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { EmailConfigurationTableComponent } from './email-configuration-table.component';
import { normalizeEmailTemplateHtml } from '../edit-email-template/email-template-body.util';
import {
  compileEmailSubjectPreview,
  parseMacroKeys,
  renderEmailPreviewInIframe,
} from '../edit-email-template/email-template-preview.util';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';

const PAGE_SIZE = 10;

@Component({
  selector: 'app-email-configuration',
  standalone: true,
  imports: [
    CommonModule,
    IconModule,
    ListModuleSurfaceComponent,
    EmailConfigurationTableComponent,
    HasPermissionPipe,
  ],
  templateUrl: './email-configuration.component.html',
})
export class EmailConfigurationComponent implements OnInit, OnDestroy, AfterViewChecked {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  @ViewChild('listPreviewFrame') listPreviewFrame?: ElementRef<HTMLIFrameElement>;

  private readonly api = inject(EmailTemplateApiService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroy$ = new Subject<void>();
  private readonly filterChanges$ = new Subject<ListModuleFilters>();
  private previewRow: EmailTemplateRow | null = null;
  private previewRenderedForId: number | null = null;

  readonly listSurfaceConfig: ListModuleSurfaceConfig = {
    toolbar: {
      search: {
        placeholder: 'Search template name or subject…',
        debounceMs: 400,
      },
      status: false,
      dateRange: false,
    },
    pagination: true,
  };

  listFilters: ListModuleFilters = { ...DEFAULT_LIST_MODULE_FILTERS };
  rows: EmailTemplateRow[] = [];
  loading = false;
  errorMessage = '';
  page = 1;
  readonly pageSize = PAGE_SIZE;
  meta: ListModulePaginationMeta | null = null;

  previewOpen = false;
  previewSubject = '';

  ngAfterViewChecked(): void {
    if (!this.previewOpen || !this.previewRow) {
      return;
    }
    if (this.previewRenderedForId === this.previewRow.id) {
      return;
    }
    const macroKeys = parseMacroKeys(this.previewRow.macro);
    this.previewSubject = compileEmailSubjectPreview(
      this.previewRow.subject ?? '',
      macroKeys,
    );
    renderEmailPreviewInIframe(
      this.listPreviewFrame?.nativeElement,
      normalizeEmailTemplateHtml(
        this.previewRow.html_content ?? '',
        this.previewRow.template_name ?? 'Email',
      ),
      macroKeys,
    );
    this.previewRenderedForId = this.previewRow.id;
  }

  ngOnInit(): void {
    this.filterChanges$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(
          (a, b) =>
            a.searchQuery === b.searchQuery &&
            a.status === b.status &&
            a.dateRangeStart === b.dateRangeStart &&
            a.dateRangeEnd === b.dateRangeEnd,
        ),
        filter((f) => !f.searchQuery || f.searchQuery.length >= 3 || f.searchQuery.length === 0),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.page = 1;
        this.loadTemplates();
      });

    this.loadTemplates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onListFiltersChange(filters: Partial<ListModuleFilters>): void {
    this.listFilters = { ...this.listFilters, ...filters };
    this.filterChanges$.next(this.listFilters);
  }

  onListPageChange(page: number): void {
    this.page = page;
    this.loadTemplates();
  }

  onToggleStatus(row: EmailTemplateRow): void {
    const next = !row.is_active;
    this.api
      .toggleTemplateStatus(row.id, next)
      .pipe(assertSuccessEnvelope(), takeUntil(this.destroy$))
      .subscribe({
        next: (env) => {
          row.is_active = next;
          this.snackBar.open(
            env.message || `Template ${next ? 'activated' : 'deactivated'}.`,
            'Dismiss',
            { duration: 4000 },
          );
        },
        error: (err) => {
          this.snackBar.open(
            resolveApiErrorMessage(err) || 'Could not update template status.',
            'Dismiss',
            { duration: 5000 },
          );
        },
      });
  }

  openPreview(row: EmailTemplateRow): void {
    this.previewRow = row;
    this.previewRenderedForId = null;
    this.previewOpen = true;
  }

  closePreview(): void {
    this.previewOpen = false;
    this.previewRow = null;
    this.previewRenderedForId = null;
    this.previewSubject = '';
  }

  private loadTemplates(): void {
    this.errorMessage = '';
    this.loading = true;
    this.api
      .listTemplates({
        page: this.page,
        page_size: this.pageSize,
        searchText: this.listFilters.searchQuery.trim() || undefined,
      })
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => (this.loading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (env) => {
          const data = env.data;
          this.rows = data?.result ?? [];
          this.meta = data?.meta ?? null;
          if (data?.meta?.currentPage) {
            this.page = data.meta.currentPage;
          }
        },
        error: (err) => {
          this.rows = [];
          this.meta = null;
          this.errorMessage =
            resolveApiErrorMessage(err) || 'Could not load email templates.';
        },
      });
  }
}
